import { BidderAddress } from '@/types/BidderAddresses';
import {
    COMPLETE_ACCOUNT_SUCCESS,
    LOAD_BIDDER_DATA_FAIL,
    LOAD_BIDDER_DATA_REQUEST,
    LOAD_BIDDER_DATA_SUCCESS,
    LOG_OUT_BIDDER,
    LOGIN_FAIL,
    LOGIN_REQUEST,
    LOGIN_SUCCESS,
} from './actions';
import { CONFIRM_PASSWORD_SUCCESS } from './confirmPassword';
import { CREATE_ACCOUNT_FAIL, CREATE_ACCOUNT_REQUEST, CREATE_ACCOUNT_SUCCESS } from './createAccount';
import { createSelector } from '@reduxjs/toolkit';
import { differenceInDays, parseISO } from 'date-fns';
import { EmptyUserData, UserData } from '@/types/UserData';
import { fetchBidderData as fetchBidderDataApi } from '@/redux/api/bidder';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import { loadBidderData } from './bidderData';
import { OPEN_CONFIRM_PASSWORD_MODAL_FROM_CHECKER } from './shouldLogin';
import { PASSWORD_RESET_REQUEST, PASSWORD_RESET_SUCCESS } from './passwordReset';
import { recordAnalyticsForIdentify } from './analytics';
import { SUBMIT_CONSENT_SUCCESS } from './consent';
import { v4 as uuidv4 } from 'uuid';
import caseInsensitiveCompare from '@liveauctioneers/caterwaul-components/lib/utils/caseInsensitiveCompare';
import Cookies from 'universal-cookie';
import jwtdecode from 'jwt-decode';
import ms from 'ms';

const cookie = new Cookies();
const REDUX_STORE_TIME = ms('30m');
const UNREGISTERED = 'unregistered';
const PARTIALLYREGISTEREDBIDDER = 'PartiallyRegisteredBidder';
const REGISTEREDBIDDER = 'bidder';

type JWTPayload = {
    sub: string;
    type: string;
};

/* reducer */
export type State = {
    confirmPasswordRequiredTriggered: boolean;
    loaded: number;
    loading: boolean;
    mobile: string;
    token: string;
    userData: UserData;
};

export const DEFAULT_STATE: State = {
    confirmPasswordRequiredTriggered: false,
    loaded: 0,
    loading: false,
    mobile: '',
    token: '',
    userData: EmptyUserData,
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}): State {
    let time;
    let bids: number;

    switch (action.type) {
        case CREATE_ACCOUNT_SUCCESS:
            time = action.meta.actionTime;
            return {
                ...state,
                loaded: time,
                loading: false,
                token: action.payload,
            };
        case LOGIN_SUCCESS:
        case CONFIRM_PASSWORD_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
            time = action.meta.actionTime;

            if (typeof action.payload.annualNMS !== 'undefined') {
                action.payload.bidder.ANMS = action.payload.annualNMS;
            }

            bids = action?.payload?.biddingInfos?.length;

            return {
                ...state,
                confirmPasswordRequiredTriggered: false,
                loaded: time,
                loading: false,
                token: action.payload.token,
                userData: {
                    ...EmptyUserData,
                    ...action.payload.bidder,
                    bidderId: Number(action.payload.bidder.bidderId),
                    upcomingBids: bids || state?.userData?.upcomingBids || 0,
                },
            };
        case COMPLETE_ACCOUNT_SUCCESS:
            time = action.meta.actionTime;
            return {
                ...state,
                loaded: time,
                loading: false,
                userData: {
                    ...EmptyUserData,
                    ...action.payload,
                },
            };
        case LOAD_BIDDER_DATA_REQUEST:
        case LOGIN_REQUEST:
        case CREATE_ACCOUNT_REQUEST:
        case PASSWORD_RESET_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_BIDDER_DATA_SUCCESS:
            time = action.meta.actionTime;

            if (typeof action.payload.annualNMS !== 'undefined') {
                action.payload.bidder.ANMS = action.payload.annualNMS;
            }

            bids = action?.payload?.biddingInfos?.length;

            return {
                ...state,
                loaded: time,
                loading: false,
                userData: {
                    ...EmptyUserData,
                    ...action.payload.bidder,
                    bidderId: Number(action.payload.bidder.bidderId),
                    upcomingBids: bids || state?.userData?.upcomingBids || 0,
                },
            };
        case OPEN_CONFIRM_PASSWORD_MODAL_FROM_CHECKER:
            return {
                ...state,
                confirmPasswordRequiredTriggered: true,
            };
        case CREATE_ACCOUNT_FAIL:
        case LOG_OUT_BIDDER:
        case LOGIN_FAIL:
            return DEFAULT_STATE;
        case SUBMIT_CONSENT_SUCCESS:
            return {
                ...state,
                userData: {
                    ...state.userData,
                    acceptedTermsOfService: action.payload,
                },
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.user;
const passThroughSelector = (state: GlobalState, passThroughValue) => passThroughValue;

export const userDataSelector = createSelector(stateSelector, (state) => state.userData);

export const getANMS = createSelector(userDataSelector, (userData) => (userData.ANMS ? userData.ANMS : 0));

export const getAuthToken = createSelector(stateSelector, (state) => state.token);

export const getBidderEmail = createSelector(userDataSelector, (userData: UserData) =>
    userData?.email ? userData?.email : ''
);

export const getBidderUpcomingBids = createSelector(
    userDataSelector,
    (userData: UserData) => userData?.upcomingBids || 0
);

export const getBidderPhoneNumber = createSelector(userDataSelector, (userData: UserData) =>
    userData?.mobileNumber ? userData?.mobileNumber : userData?.phoneNumber ? userData?.phoneNumber : ''
);

export const isTermsOfServiceUnknown = createSelector(
    userDataSelector,
    (userData: UserData) => userData?.acceptedTermsOfService === 'unknown'
);

export const getBidderCountry = createSelector(userDataSelector, (userData: UserData) =>
    userData.country ? userData.country : ''
);

export const getBidderState = createSelector(userDataSelector, (userData: UserData) =>
    userData.state ? userData.state : ''
);

export const getBidderInUSA = createSelector(
    getBidderCountry,
    (country: string = '') => caseInsensitiveCompare(country, 'us') || caseInsensitiveCompare(country, 'united states')
);

const nonContiguousState = [
    'Hawaii',
    'HI',
    'Alaska',
    'AK',
    'American Samoa',
    'AS',
    'Virgin Islands',
    'VI',
    'Northern Mariana Islands',
    'MP',
    'Guam',
    'GU',
    'Puerto Rico',
    'PR',
];

export const isBidderInContiguousUS = createSelector(
    [getBidderCountry, getBidderState],
    (country: string, state: string) => ['United States', 'US'].includes(country) && !nonContiguousState.includes(state)
);

export const getBidderType = createSelector(getAuthToken, (token) => {
    if (token) {
        try {
            // TODO: see if this try catch is enough.  We shouldn't throw if something is wrong
            return jwtdecode<JWTPayload>(token).type;
        } catch (error) {
            return UNREGISTERED;
        }
    }
    return UNREGISTERED;
});

export const isConfirmPasswordTriggered = createSelector(
    stateSelector,
    (state) => state.confirmPasswordRequiredTriggered
);

export const isUserPartiallyRegistered = createSelector(
    getBidderType,
    (bidderType) => bidderType === PARTIALLYREGISTEREDBIDDER
);

const isUserRegistered = createSelector(getBidderType, (bidderType) => bidderType === REGISTEREDBIDDER);

const validateBidderId = (id: number) => {
    if (typeof id !== 'number' || isNaN(id) || !id) {
        return -1;
    }
    return id;
};

export const getBidderId: (state: GlobalState) => number = createSelector(
    [userDataSelector, getAuthToken],
    (userData: UserData, token: string) => {
        if (!Boolean(userData?.bidderId) || userData?.bidderId <= 0) {
            if (token) {
                try {
                    const decoded = jwtdecode<JWTPayload>(token);

                    if (decoded.type === 'bidder') {
                        return validateBidderId(Number(decoded.sub));
                    }
                } catch (error) {
                    console.warn('Failed to decode a potentially valid token', error, token);
                }
            }
            return -1;
        }

        return validateBidderId(Number(userData.bidderId));
    }
);

export const isCookieAuthTokenMismatched = createSelector(
    [getBidderId, getAuthToken],
    (reduxBidderId, reduxAuthToken: string): boolean => {
        try {
            const cookie = new Cookies();
            const cookieAuthToken = cookie.get('bidder-auth');

            if (cookieAuthToken && reduxAuthToken) {
                const decodedCookieToken = jwtdecode<JWTPayload>(cookieAuthToken);

                if (decodedCookieToken.type === 'bidder') {
                    const cookieBidderId = validateBidderId(Number(decodedCookieToken.sub));

                    // the bidderId from redux + cookie match, everything is fine
                    if (cookieBidderId === reduxBidderId) {
                        return false;
                    }

                    // the bidderIds did not match, something is wrong
                    return true;
                }
            } else {
                return false;
            }
        } catch (error) {
            console.warn('Failed to decode a potentially valid token', error, reduxAuthToken);

            return true;
        }
    }
);

export const isUserLoggedIn = createSelector(
    getAuthToken,
    getBidderId,
    isUserRegistered,
    (token, bidderId, registered): boolean => Boolean(token) && registered && bidderId > 0
);

export const isUserAtLeastPartiallyRegistered = createSelector(
    isUserPartiallyRegistered,
    isUserLoggedIn,
    (registered, loggedIn) => registered || loggedIn
);

export const getUserData = createSelector(
    isUserAtLeastPartiallyRegistered,
    userDataSelector,
    (userAtLeastPartiallyRegistered, userData) => (userAtLeastPartiallyRegistered ? userData : EmptyUserData)
);

export const getUserIsInGoodStanding = createSelector(getUserData, (userData: UserData) => {
    const standing = userData.standing || '';
    return standing === '' || standing === 'y' || standing === 'good';
});

export const getBidderIsSuspended = createSelector(getUserData, (userData: UserData) => {
    const standing = userData.standing || '';
    return standing === 's';
});

export const getUserFullName = createSelector(getUserData, (userData: UserData) => {
    const { firstName = '', lastName = '' } = userData;
    return `${firstName} ${lastName}`.trim();
});

export const getUserFirstName = createSelector(getUserData, (userData: UserData) => {
    const first = userData.firstName || '';
    return first.trim();
});

export const getUserLastName = createSelector(getUserData, (userData: UserData) => {
    const last = userData.lastName || '';
    return last.trim();
});

export const getZipCodeGeoCoordinates = createSelector(getUserData, (userData: UserData) =>
    Boolean(userData?.latitude) && Boolean(userData?.longitude)
        ? { latitude: userData.latitude, longitude: userData.longitude }
        : null
);

export const isBidderLoaded = createSelector(userDataSelector, (userData: UserData) => userData?.bidderId > 0);

export const isProfileComplete = createSelector(userDataSelector, (userData: UserData) =>
    Boolean(userData?.profileComplete)
);

const getLoadTimeForUserData: (state: GlobalState) => number = createSelector(
    stateSelector,
    (state) => state.loaded || 0
);

export const isUserDataLoading = createSelector(stateSelector, (state) => state.loading || false);

export const getUserDataLoaded = createSelector(stateSelector, (state) => state.loaded > 0 || false);

export const isUserOlderThanDays: (state: GlobalState, days: number) => boolean = createSelector(
    userDataSelector,
    passThroughSelector,
    (userData, days) => {
        if (!Boolean(userData)) {
            return false;
        }
        return differenceInDays(new Date(), parseISO(userData.createdDate)) >= days;
    }
);

export const getUserAddressById: (state: GlobalState, addressId: number) => BidderAddress | null = createSelector(
    userDataSelector,
    passThroughSelector,
    (userData, addressId) =>
        Boolean(userData?.addresses?.length)
            ? userData?.addresses.find((address) => address.addressId === addressId)
            : null
);

export const getDefaultAddressId = createSelector(userDataSelector, (userData): number => {
    if (!Boolean(userData.addresses?.length)) {
        return 0;
    }
    const defaultAddress = userData.addresses.find((address) => address.isDefault);
    return defaultAddress?.addressId || 0;
});

export const getSessionId: () => string = () => `${cookie.get('_session_id') || uuidv4()}`;

const shouldFetchUserData = (state: GlobalState): boolean => {
    const userAtLeastPartiallyRegistered = isUserAtLeastPartiallyRegistered(state);
    if (!userAtLeastPartiallyRegistered) {
        return false;
    }

    const userData = getUserData(state);
    if (userData) {
        const loaded = getLoadTimeForUserData(state);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isUserDataLoading(state);
    return !loading;
};

/* ACTION CREATORS */
export const logOutBidder = () => ({
    type: LOG_OUT_BIDDER,
});

const fetchBidderData =
    ({ authToken, deployment }: { authToken: string; deployment: string }) =>
    async (dispatch: Function) => {
        try {
            dispatch({
                payload: authToken,
                type: LOAD_BIDDER_DATA_REQUEST,
            });
            const response = await fetchBidderDataApi({ authToken, deployment });

            await dispatch(loadBidderData(response.data.resellerType));

            await dispatch({
                meta: { actionTime: Date.now() },
                payload: response.data,
                type: LOAD_BIDDER_DATA_SUCCESS,
            });
            return dispatch(recordAnalyticsForIdentify());
        } catch (error) {
            dispatch({
                error: true,
                meta: { token: authToken },
                payload: error,
                type: LOAD_BIDDER_DATA_FAIL,
            });
            return dispatch(logOutBidder());
        }
    };

export const fetchBidderDataIfNeeded =
    (token?: string | null | undefined, force?: boolean) => async (dispatch: Function, getState: Function) => {
        const state = getState();
        const authToken = token || getAuthToken(state);
        const deployment = getDeployment(state);
        if (Boolean(authToken) && (force || shouldFetchUserData(state))) {
            await dispatch(fetchBidderData({ authToken, deployment }));
        }
        return Promise.resolve();
    };
